import { useEffect, useState } from "react";
import axios from "axios";
import { v4 as uuid } from "uuid";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

/* ---------- helpers ---------- */
const findPath = (items, id, path = []) => {
    for (let i = 0; i < items.length; i++) {
        const node = items[i];
        const next = [...path, i];
        if (node.id === id) return next;
        if (node.children) {
            const res = findPath(node.children, id, [...next, "children"]);
            if (res) return res;
        }
    }
    return null;
};
const getAtPath = (obj, path) => path.reduce((a, k) => a?.[k], obj);

/* ---------- main component ---------- */

export default function AdminNavEditor({ editor = "public" }) {
    const [tree, setTree] = useState([]);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [editItemIndex, setEditItemIndex] = useState(null);
    const [pages, setPages] = useState([]);
    const [editing, setEditing] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const headers = {
                    Authorization: `Bearer ${token}`,
                };
                
                if (editor === "public") {
                    axios.get("/api/navbar", { headers }).then((r) => setTree(r.data));
                    axios.get("/api/content", { headers }).then((r) => setPages(r.data));
                } else {
                    axios.get("/api/member-navbar", { headers }).then((r) => setTree(r.data));
                    axios.get("/api/member-content", { headers }).then((r) => setPages(r.data));
                }
            } catch (error) {
                console.error("Error fetching navigation data:", error);
            }
        };
        
        fetchData();
    }, [editor]);

    /* save entire nav */
    const save = async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            if (editor === "public") {
                await axios.put("/api/navbar", tree, { headers });
            } else {
                await axios.put("/api/member-navbar", tree, { headers });
            }
            toast.success("Changes saved!");
        } catch (error) {
            console.error("Error saving navigation:", error);
            toast.error("Error saving changes");
        }
    };

    /* add link and auto‑open editor */
    const addItem = () => {
        const newNode = { id: uuid(), label: "New Link", slug: "", children: [] };
        setTree([...tree, newNode]);
        setEditing(newNode);       // auto‑open editor
    };

    /* delete (with confirm) */
    const deleteItem = (id) => {
        const prune = (list) =>
            list
                .filter((n) => n.id !== id)
                .map((n) => ({ ...n, children: prune(n.children || []) }));
        setEditing(null);
        setTree(prune(tree));
        toast.warn("Link deleted, but Navbar changes not yet saved");
    };

    /* basic swap */
    const swap = (arr, i, j) => {
        const tmp = arr[i];
        arr[i] = arr[j];
        arr[j] = tmp;
    };

    /* move up/down */
    const move = (id, dir) => {
        const path = findPath(tree, id);
        if (!path) return;
        const idx = path[path.length - 1];
        const parent = getAtPath(tree, path.slice(0, -1));
        if (dir === "up" && idx > 0) swap(parent, idx, idx - 1);
        if (dir === "down" && idx < parent.length - 1) swap(parent, idx, idx + 1);
        setTree([...tree]);
    };

    /* indent ► */
    const indent = (id) => {
        const path = findPath(tree, id);
        if (!path) return;
        const idx = path[path.length - 1];
        const parent = getAtPath(tree, path.slice(0, -1));
        if (idx === 0) return;
        const prev = parent[idx - 1];
        prev.children = prev.children || [];
        prev.children.push(parent.splice(idx, 1)[0]);
        setTree([...tree]);
    };

    /* outdent ◄ */
    const outdent = (id) => {
        const path = findPath(tree, id);
        if (!path || path.length < 3) return;
        const idx = path[path.length - 1];
        const parentArr = getAtPath(tree, path.slice(0, -1));
        const node = parentArr.splice(idx, 1)[0];
        const grandArr = getAtPath(tree, path.slice(0, -3));
        const parentIdx = path[path.length - 3];
        grandArr.splice(parentIdx + 1, 0, node);
        setTree([...tree]);
    };

    /* ---------- editor panel ---------- */
    const EditorPanel = ({ node }) => {
        const [label, setLabel] = useState(node.label);
        const [slug, setSlug] = useState(node.slug || "");

        const saveNode = () => {
            node.label = label;
            node.slug = slug;
            setTree([...tree]);
            setEditing(null);
            toast.warn("Edits confirmed, but Navbar changes not yet saved")
        };

        return (
            <div className="card mt-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">Edit Navigation Item</h5>

                    <div className="mb-3">
                        <label className="form-label fw-bold">Label</label>
                        <input
                            className="form-control"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-bold">Page</label>
                        <select
                            className="form-select"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                        >
                            <option value="">(No link)</option>
                            {pages.map((p) => (
                                <option key={p.slug} value={p.slug}>
                                    {p.title}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button className="btn btn-admin me-2" onClick={saveNode}>
                        Confirm
                    </button>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => setEditing(null)}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
    };

    /* ---------- recursive list ---------- */
    const renderList = (items, depth = 0) =>
        items.map((n) => (
            <li
                key={n.id}
                className="list-group-item border-0"
                style={{ marginLeft: depth * 20 }}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>{n.label}</strong>{" "}
                        {n.slug !== null && <span className="text-muted ms-2">/{n.slug}</span>}
                    </div>

                    <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" onClick={() => move(n.id, "up")}>↑</button>
                        <button className="btn btn-outline-secondary" onClick={() => move(n.id, "down")}>↓</button>
                        <button className="btn btn-outline-secondary" onClick={() => outdent(n.id)}>←</button>
                        <button className="btn btn-outline-secondary" onClick={() => indent(n.id)}>→</button>
                        <button className="btn btn-outline-secondary" onClick={() => setEditing(n)}>✎</button>
                        <button className="btn btn-outline-danger" onClick={() => deleteItem(n.id)}>✕</button>
                    </div>
                </div>

                {n.children?.length > 0 && (
                    <ul className="list-group mt-1">{renderList(n.children, depth + 1)}</ul>
                )}
            </li>
        ));

    /* ---------- UI ---------- */
    return (
        <div className="container mt-2">
            <p className="text-body-secondary">This edits the {editor === "public" ? "public" : "member"} site's top Navbar. Create pages in the Admin Dashboard and they will show up as options here.</p>

            <button className="btn btn-custom-outline mb-2" onClick={addItem}>
                + Add Link
            </button>

            <div className="card mt-2">
                <ul className="list-group">{renderList(tree)}</ul>
            </div>

            {editing && <EditorPanel node={editing} />}

            <button className="btn btn-admin mt-4" onClick={save}>
                Save Navigation
            </button>
        </div>
    );
}
